import React, { useState, useEffect, useMemo } from 'react';
import { FiEdit2, FiX, FiFileText, FiPlus } from 'react-icons/fi';
import { getClients, createClient, updateClient, deleteClient, getConfig } from '../../../services/api';
import '../../../styles/ModuleTable.css';

const BLANK = {
  document_type: '',
  document_number: '',
  nombres: '',
  last_name: '',
  second_last_name: '',
  phone: '',
  email: '',
  legal_rep: '',
  address: '',
};

const ITEMS_OPTIONS = [10, 20, 50, 100];

// --- helpers -----------------------------------------------------------------
function buildFullName(nombres, ap, am) {
  return [ap, am, nombres].filter(Boolean).join(' ').toUpperCase();
}

function isOnlyNumbers(val) { return /^[0-9]*$/.test(val); }

function exportToExcel(headers, rows, filename) {
  let html = `<table><thead><tr>${headers.map(h => `<th>${h}</th>`).join('')}</tr></thead><tbody>`;
  rows.forEach(r => { html += `<tr>${r.map(c => `<td>${c ?? ''}</td>`).join('')}</tr>`; });
  html += '</tbody></table>';
  const blob = new Blob(['﻿' + html], { type: 'application/vnd.ms-excel' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `${filename}.xls`;
  a.click();
  URL.revokeObjectURL(a.href);
}

function exportToPDF(title, headers, rows) {
  const win = window.open('', '_blank');
  const trs = rows.map((r, i) =>
    `<tr style="background:${i % 2 === 0 ? '#fff' : '#f0f7ff'}">${r.map(c => `<td>${c ?? ''}</td>`).join('')}</tr>`
  ).join('');
  win.document.write(`<html><head><title>${title}</title>
    <style>body{font-family:Arial,sans-serif;font-size:11px;padding:16px}
    h2{color:#17375e;font-size:13px;margin-bottom:8px;text-transform:uppercase}
    table{border-collapse:collapse;width:100%}
    th{background:#dce6f1;color:#17375e;padding:6px 8px;text-align:left;border:1px solid #9ebcdf;font-size:11px}
    td{padding:5px 8px;border:1px solid #dde8f2;font-size:11px}
    .footer{margin-top:10px;font-size:10px;color:#888;text-align:right}</style></head>
    <body><h2>${title}</h2>
    <table><thead><tr>${headers.map(h => `<th>${h}</th>`).join('')}</tr></thead>
    <tbody>${trs}</tbody></table>
    <div class="footer">PIVASAPOLLOS — Generado ${new Date().toLocaleString('es-PE', { timeZone: 'America/Lima' })}</div>
    <script>window.onload=()=>{window.print();}</script></body></html>`);
  win.document.close();
}

function Paginacion({ page, totalPages, perPage, setPage, setPerPage }) {
  return (
    <div className="mt-pagination">
      <span className="pg-info">({page} of {totalPages})</span>
      <button onClick={() => setPage(1)} disabled={page === 1}>|&lt;</button>
      <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>&lt;&lt;</button>
      <span className="pg-current">{page}</span>
      <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>&gt;&gt;</button>
      <button onClick={() => setPage(totalPages)} disabled={page === totalPages}>&gt;|</button>
      <select value={perPage} onChange={e => { setPerPage(+e.target.value); setPage(1); }}>
        {ITEMS_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
      </select>
    </div>
  );
}

// --- componente principal ----------------------------------------------------
function ClientMaintenance({ businessId }) {
  const [docTypes, setDocTypes] = useState([]);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const [filterDocType, setFilterDocType] = useState('');
  const [filterNumero, setFilterNumero] = useState('');
  const [filterNombre, setFilterNombre] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  useEffect(() => {
    load();
    getConfig('tipos-documento', businessId)
      .then(res => setDocTypes(res.data.data || []))
      .catch(() => setDocTypes([]));
  }, [businessId]); // eslint-disable-line

  const load = async () => {
    setLoading(true);
    try {
      const res = await getClients(businessId);
      setData(res.data.data || []);
    } catch (e) {
      alert('Error al cargar clientes: ' + (e.response?.data?.error || e.message));
    } finally { setLoading(false); }
  };

  const filtered = useMemo(() => {
    return data.filter(r => {
      if (filterDocType && !(r.document_type || '').includes(filterDocType)) return false;
      if (filterNumero && !(r.document_number || '').includes(filterNumero)) return false;
      const fn = (r.nombre_completo || '').toUpperCase();
      if (filterNombre && !fn.includes(filterNombre.toUpperCase())) return false;
      if (search) {
        const s = search.toUpperCase();
        return fn.includes(s)
          || (r.document_number || '').includes(s)
          || (r.phone || '').includes(s)
          || (r.address || '').toUpperCase().includes(s);
      }
      return true;
    });
  }, [data, filterDocType, filterNumero, filterNombre, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const paged = filtered.slice((page - 1) * perPage, page * perPage);

  // --- tipo doc helpers -------------------------------------------------------
  const isDNI = form.document_type.includes('DNI');
  const isRUC = form.document_type.includes('RUC');

  // Cuando cambia un campo de nombre Y es DNI → autocompletar legal_rep
  const setNameField = (field, value) => {
    setForm(prev => {
      const next = { ...prev, [field]: value };
      if (prev.document_type.includes('DNI')) {
        const nombres = field === 'nombres' ? value : prev.nombres;
        const ap      = field === 'last_name' ? value : prev.last_name;
        const am      = field === 'second_last_name' ? value : prev.second_last_name;
        next.legal_rep = buildFullName(nombres, ap, am);
      }
      return next;
    });
    setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleDocTypeChange = (e) => {
    const val = e.target.value;
    setForm(prev => {
      const next = { ...prev, document_type: val, document_number: '' };
      if (val.includes('RUC')) {
        // Limpiar apellidos — no aplican para empresa
        next.last_name = '';
        next.second_last_name = '';
      } else if (val.includes('DNI')) {
        next.legal_rep = buildFullName(prev.nombres, prev.last_name, prev.second_last_name);
      }
      return next;
    });
    setErrors({});
  };

  // Solo números para teléfono
  const handlePhone = (e) => {
    const val = e.target.value.replace(/[^0-9]/g, '');
    setForm(prev => ({ ...prev, phone: val }));
    setErrors(prev => ({ ...prev, phone: '' }));
  };

  // Solo números para DNI/RUC; libre para otros docs
  const handleDocNum = (e) => {
    const raw = e.target.value;
    const val = (isDNI || isRUC) ? raw.replace(/[^0-9]/g, '') : raw;
    setForm(prev => ({ ...prev, document_number: val }));
    setErrors(prev => ({ ...prev, document_number: '' }));
  };

  // --- validación antes de guardar -------------------------------------------
  const validate = () => {
    const errs = {};

    if (isDNI && form.document_number && form.document_number.length !== 8)
      errs.document_number = 'DNI debe tener exactamente 8 dígitos';
    else if (isRUC && form.document_number && form.document_number.length !== 11)
      errs.document_number = 'RUC debe tener exactamente 11 dígitos';
    else if ((isDNI || isRUC) && form.document_number && !isOnlyNumbers(form.document_number))
      errs.document_number = 'Solo se permiten números';

    if (form.phone && !isOnlyNumbers(form.phone))
      errs.phone = 'Solo se permiten números';

    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      errs.email = 'Correo electrónico inválido';

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // --- abrir modal -----------------------------------------------------------
  const openNew = () => { setForm(BLANK); setErrors({}); setEditId(null); setModal(true); };

  const openEdit = (row) => {
    setErrors({});
    setForm({
      document_type:    row.document_type    || '',
      document_number:  row.document_number  || '',
      nombres:          row.nombres          || '',
      last_name:        row.apellido_paterno || '',
      second_last_name: row.apellido_materno || '',
      phone:            row.phone            || '',
      email:            row.email            || '',
      legal_rep:        row.legal_rep        || '',
      address:          row.address          || '',
    });
    setEditId(row.id);
    setModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Desea eliminar este cliente?')) return;
    try { await deleteClient(id, businessId); load(); }
    catch (e) { alert('Error: ' + (e.response?.data?.error || e.message)); }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    const payload = {
      document_type:    form.document_type,
      document_number:  form.document_number,
      nombres:          form.nombres,
      apellido_paterno: form.last_name,
      apellido_materno: form.second_last_name,
      phone:            form.phone,
      email:            form.email,
      legal_rep:        form.legal_rep,
      address:          form.address,
    };
    try {
      editId
        ? await updateClient(editId, businessId, payload)
        : await createClient(businessId, payload);
      setModal(false);
      load();
    } catch (e) {
      alert('Error: ' + (e.response?.data?.error || e.message));
    } finally { setSaving(false); }
  };

  // --- exports ---------------------------------------------------------------
  const excelData = () => {
    const headers = ['TIPO DOCUMENTO', 'NUMERO', 'DATOS CLIENTE', 'TELEFONOS', 'DIRECCIÓN'];
    exportToExcel(headers, filtered.map(r => [r.document_type, r.document_number, r.nombre_completo, r.phone, r.address]), 'Clientes');
  };
  const pdfData = () => {
    const headers = ['TIPO DOCUMENTO', 'NUMERO', 'DATOS CLIENTE', 'TELEFONOS', 'DIRECCIÓN'];
    exportToPDF('CLIENTES', headers, filtered.map(r => [r.document_type, r.document_number, r.nombre_completo, r.phone, r.address]));
  };

  const ExportBar = () => (
    <div className="mt-export-bar">
      <button className="btn-excel" onClick={excelData}><span style={{ fontSize: 14 }}>🐦</span> Excel</button>
      <button className="btn-pdf" onClick={pdfData}><FiFileText size={12} /> PDF</button>
      <Paginacion page={page} totalPages={totalPages} perPage={perPage} setPage={setPage} setPerPage={setPerPage} />
    </div>
  );

  // --- render ----------------------------------------------------------------
  return (
    <div className="mt-container">
      {/* Toolbar */}
      <div className="mt-toolbar">
        <div className="mt-toolbar-left">
          <button className="btn-nuevo" onClick={openNew}><FiPlus size={12} /> Nuevo</button>
        </div>
        <div className="mt-search-global">
          <span>Busqueda Global</span>
          <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
        </div>
      </div>

      <ExportBar />

      {/* Tabla */}
      <div className="mt-table-wrap">
        {loading ? <div className="mt-loading">Cargando...</div> : (
          <table className="mt-table mt-table-fixed">
            <colgroup>
              <col style={{ width: '16%' }} />  {/* Tipo Documento — reducida a ~mitad */}
              <col style={{ width: '11%' }} />  {/* Numero */}
              <col style={{ width: '27%' }} />  {/* Datos Cliente */}
              <col style={{ width: '11%' }} />  {/* Telefonos */}
              <col />                            {/* Direccion — ocupa el resto */}
              <col style={{ width: 34 }} />
              <col style={{ width: 34 }} />
            </colgroup>
            <thead>
              <tr className="mt-head-row">
                <th>Tipo Documento</th>
                <th>Numero</th>
                <th>Datos Cliente</th>
                <th>Telefonos</th>
                <th>Direccion</th>
                <th></th>
                <th></th>
              </tr>
              <tr className="mt-filter-row">
                <th>
                  <select value={filterDocType} onChange={e => { setFilterDocType(e.target.value); setPage(1); }}>
                    <option value="">Seleccione un valor</option>
                    {docTypes.map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
                  </select>
                </th>
                <th><input value={filterNumero} onChange={e => { setFilterNumero(e.target.value); setPage(1); }} /></th>
                <th><input value={filterNombre} onChange={e => { setFilterNombre(e.target.value); setPage(1); }} /></th>
                <th></th><th></th><th></th><th></th>
              </tr>
            </thead>
            <tbody>
              {paged.length === 0
                ? <tr className="mt-empty"><td colSpan={7}>Sin registros</td></tr>
                : paged.map((row, i) => (
                  <tr key={row.id} className={i % 2 === 0 ? 'mt-row-even' : 'mt-row-odd'}>
                    <td className="td-truncate" title={row.document_type}>{row.document_type}</td>
                    <td>{row.document_number}</td>
                    <td className="td-truncate" title={row.nombre_completo}>{row.nombre_completo}</td>
                    <td>{row.phone}</td>
                    <td className="td-truncate" title={row.address}>{row.address}</td>
                    <td className="td-actions">
                      <button className="btn-row-edit" onClick={() => openEdit(row)} title="Editar"><FiEdit2 size={13} /></button>
                    </td>
                    <td className="td-actions">
                      <button className="btn-row-del" onClick={() => handleDelete(row.id)} title="Eliminar"><FiX size={14} /></button>
                    </td>
                  </tr>
                ))
              }
            </tbody>
          </table>
        )}
      </div>

      <ExportBar />

      {/* Modal */}
      {modal && (
        <div className="mt-modal-overlay">
          <div className="mt-modal" style={{ maxWidth: 560 }}>
            <div className="mt-modal-header">
              <span>Mantenimiento Clientes</span>
              <button className="btn-modal-close" onClick={() => setModal(false)}>✕</button>
            </div>
            <form onSubmit={handleSave}>
              <div className="mt-modal-body">
                <div className="mt-form-grid">

                  {/* Tipo Documento */}
                  <div className="mt-field">
                    <label>Tipo Documento <span className="req">*</span></label>
                    <select value={form.document_type} onChange={handleDocTypeChange} required>
                      <option value="">Seleccione...</option>
                      {docTypes.map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
                    </select>
                  </div>

                  {/* Nro Documento */}
                  <div className="mt-field">
                    <label>
                      Nro Documento <span className="req">*</span>
                      {isDNI && <span className="field-hint"> (8 dígitos)</span>}
                      {isRUC && <span className="field-hint"> (11 dígitos)</span>}
                    </label>
                    <input
                      value={form.document_number}
                      onChange={handleDocNum}
                      maxLength={isDNI ? 8 : isRUC ? 11 : undefined}
                      inputMode={(isDNI || isRUC) ? 'numeric' : 'text'}
                      required
                    />
                    {errors.document_number && <span className="field-error">{errors.document_number}</span>}
                  </div>

                  {/* Nombres / Nombre de Empresa */}
                  <div className={`mt-field${isRUC ? ' mt-full' : ''}`}>
                    <label>
                      {isRUC ? 'Nombre de la Empresa' : 'Nombres'}
                      {' '}<span className="req">*</span>
                    </label>
                    <input
                      value={form.nombres}
                      onChange={e => setNameField('nombres', e.target.value)}
                      required
                    />
                  </div>

                  {/* Apellido Paterno — oculto para RUC */}
                  {!isRUC && (
                    <div className="mt-field">
                      <label>Apellido Paterno</label>
                      <input
                        value={form.last_name}
                        onChange={e => setNameField('last_name', e.target.value)}
                      />
                    </div>
                  )}

                  {/* Apellido Materno — oculto para RUC */}
                  {!isRUC && (
                    <div className="mt-field">
                      <label>Apellido Materno</label>
                      <input
                        value={form.second_last_name}
                        onChange={e => setNameField('second_last_name', e.target.value)}
                      />
                    </div>
                  )}

                  {/* Telefonos */}
                  <div className="mt-field">
                    <label>Telefonos</label>
                    <input
                      value={form.phone}
                      onChange={handlePhone}
                      inputMode="numeric"
                      placeholder="Solo números"
                    />
                    {errors.phone && <span className="field-error">{errors.phone}</span>}
                  </div>

                  {/* Correo */}
                  <div className="mt-field">
                    <label>Correo Electrónico</label>
                    <input
                      type="text"
                      value={form.email}
                      onChange={e => { setForm(p => ({ ...p, email: e.target.value })); setErrors(p => ({ ...p, email: '' })); }}
                      placeholder="ejemplo@correo.com"
                    />
                    {errors.email && <span className="field-error">{errors.email}</span>}
                  </div>

                  {/* Representante Legal — se auto-completa cuando es DNI */}
                  <div className="mt-field">
                    <label>
                      Representante Legal
                      {isDNI && <span className="field-hint"> (autogenerado)</span>}
                    </label>
                    <input
                      value={form.legal_rep}
                      onChange={e => setForm(p => ({ ...p, legal_rep: e.target.value }))}
                      style={isDNI ? { background: '#eaf2fb', fontWeight: 600 } : {}}
                    />
                  </div>

                  {/* Domicilio */}
                  <div className="mt-field mt-full">
                    <label>Domicilio <span className="req">*</span></label>
                    <textarea
                      value={form.address}
                      onChange={e => setForm(p => ({ ...p, address: e.target.value }))}
                      required
                    />
                  </div>

                </div>
              </div>
              <div className="mt-modal-footer">
                <button type="submit" className="btn-modificar" disabled={saving}>
                  {saving ? 'Guardando...' : 'Modificar'}
                </button>
                <button type="button" className="btn-salir" onClick={() => setModal(false)}>✕ Salir</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default ClientMaintenance;
